/**
 * Home pane-default resolution — pure, so it can be unit-tested without
 * mounting the Home page. Everything Home derives from a PERSISTED intent lives
 * here: which patient the rail shows, which agent face pane 3 shows, the
 * patient frame that face works in, and which pane a phone opens on.
 *
 * The common thread is that every persisted field can outlive what it points
 * at — a different day, another nurse on the same device, an agent renamed or
 * dropped from the catalogue between deploys. Resolution validates each stored
 * id against LIVE state (the shift, the launchable catalogue) so a stale value
 * can never wedge a pane on something that no longer exists; it degrades to the
 * same default the nurse would get on a fresh install. Kept effect-free (pure
 * derivation), matching Home's derive-don't-sync style.
 */
import { sessionSurfaceSlug, type SurfacedSession } from '@/lib/sessionRouting'
import type { HomeScope } from '@/stores/homeNav'

/** True iff a stored selection points at a patient on the current shift. */
export function isPatientSelectionValid(
  selectedPatientId: string | null,
  currentPatientIds: readonly string[],
): boolean {
  return selectedPatientId != null && currentPatientIds.includes(selectedPatientId)
}

/**
 * The patient the rail should show: the stored selection when it is still on
 * shift, otherwise the first current patient (or null when the shift is empty).
 */
export function resolveEffectivePatientId(
  selectedPatientId: string | null,
  currentPatientIds: readonly string[],
): string | null {
  return (
    (isPatientSelectionValid(selectedPatientId, currentPatientIds)
      ? selectedPatientId
      : currentPatientIds[0]) ?? null
  )
}

/**
 * The agent FACE pane 3 should fall back to when the nurse has selected no
 * conversation, or null to use the "scope's latest conversation" default.
 *
 * A learn quiz RUN is skipped by that default — its transcript is
 * training_step messages that fold to nothing inline, so opening it would show
 * an empty chat strip. But SKIPPING it must not walk on to a DIFFERENT agent's
 * session: right after a quiz the newest session IS the run, so the old
 * behaviour opened whatever came next (measured on usb: Expert, then Digital
 * Twin once that was archived) — never Learn, the one agent she had just been
 * working in. Returning that run's own agent face keeps the "never an empty
 * transcript" guarantee without swapping the agent underneath her.
 *
 * `isRunSession` is injected (Home passes isLearnRunSession) so this stays a
 * pure module with no session-routing import.
 */
export function resolveFallbackAgentFace<S extends SurfacedSession>(
  newestInScope: S | undefined,
  hasExplicitSelection: boolean,
  opensOffFace: (session: S) => boolean,
): { slug: string } | null {
  if (hasExplicitSelection || !newestInScope) return null
  if (!opensOffFace(newestInScope)) return null
  return { slug: sessionSurfaceSlug(newestInScope) }
}

/**
 * The agent FACE pane 3 renders: the nurse's own launched face when that agent
 * can still be rendered, else the derived fallback, else nothing (pane 3 goes
 * back to the scope's latest conversation).
 *
 * `canRender` is what makes a PERSISTED face safe. `launchAgent` only
 * ever writes a slug the catalogue has a detail view for, but the face now
 * survives a reload — and a deploy — so the slug it holds is only as current as
 * the catalogue it was written against. This repo renames agent slugs (#2427
 * renamed `expert` → `knowledge-profile`, with db/migrations/api/045 re-pointing
 * `sessions.agent_slug`), and it can drop one outright. Without the check a
 * nurse holding a slug from the previous deploy reloads into a pane 3 that
 * renders a header and nothing else, and stays there until she taps something.
 *
 * This is a FLOOR, not a substitute for migrating: SQL cannot reach
 * localStorage, so a rename must still bump the homeNav store's persist
 * `version` with a `migrate` mapping old→new, or every nurse silently loses the
 * agent she had open. The check only guarantees that when that is forgotten —
 * or when an agent is removed and there is nothing to map to — she lands on the
 * scope default instead of a dead pane.
 */
export function resolveEffectiveAgentFace(
  selectedFace: { slug: string } | null,
  fallbackFace: { slug: string } | null,
  canRender: (slug: string) => boolean,
): { slug: string } | null {
  if (selectedFace && canRender(selectedFace.slug)) return selectedFace
  if (fallbackFace && canRender(fallbackFace.slug)) return fallbackFace
  return null
}

/** What to do with a deep-linked agent slug on THIS render — see
 *  resolveDeepLinkAdoption. */
export interface DeepLinkAdoption {
  /** Record `deepLinkedSlug` as considered (the caller stores it in a ref).
   *  False means "try again next render" — see canRender below. */
  consider: boolean
  /** Call setSelectedAgentFace({slug: deepLinkedSlug}) and skip the rest of
   *  the effect this pass. Only ever true when `consider` is also true. */
  adopt: boolean
}

/**
 * Resolves what a page should do with the agent slug it LOADED with
 * (`/home/a/<slug>/…`) on THIS render of the deep-link-adoption effect.
 *
 * `consideredSlug` records the slug a PRIOR pass already resolved (the caller
 * owns the ref) — comparing the SLUG, not a boolean "have we done this yet",
 * is what lets a second, different deep link in the same page life still
 * adopt: client-side `/home/a/protocols/…` → `/home/a/learn/…`, or Back/
 * Forward between two agent deep links, neither of which remounts Home
 * (`/home/*` is one React Router route — see App.tsx).
 *
 * `consider` is deliberately withheld while `canRender(deepLinkedSlug)` is
 * false — a pass that cannot answer "does this agent have a face" must not
 * retire the deep link, or a transiently-unrenderable slug on a cold load
 * would drop it permanently once whatever it was waiting on resolves. (In
 * this codebase canRenderAgentFace reads a static, eagerly-loaded module map
 * — see agentDetailViews.ts's `import.meta.glob(..., {eager:true})` — so it
 * is the SAME value on every render for a given slug and this branch never
 * actually retries in production; kept anyway because the function must not
 * assume that of every future `canRender` implementation callers might pass.)
 *
 * `adopt` only fires when the face doesn't already show the deep-linked slug
 * — landing on a run whose agent is ALREADY the persisted face (a reload
 * mid-session, or a previous tab) still needs `consider: true` so the SAME
 * slug being cleared later (e.g. returning a run to its parent conversation)
 * is not read as "never considered" and silently re-adopted. Found live:
 * exactly that reload-then-return sequence got the pane permanently stuck on
 * the run, because a previous version of this guard only advanced past the
 * initial sentinel on the branch that called setSelectedAgentFace.
 */
export function resolveDeepLinkAdoption(
  deepLinkedSlug: string | null,
  consideredSlug: string | null,
  effectiveFaceSlug: string | null,
  canRender: (slug: string) => boolean,
): DeepLinkAdoption {
  if (deepLinkedSlug == null || consideredSlug === deepLinkedSlug || !canRender(deepLinkedSlug)) {
    return { consider: false, adopt: false }
  }
  return { consider: true, adopt: effectiveFaceSlug !== deepLinkedSlug }
}

/**
 * The patient frame pane 3 works in: the one panes 1 and 2 already show, and
 * nothing at all outside the patient scope. The face itself stores no patient
 * (see AgentFaceSelection) — this is the single derivation all three panes
 * share, so a face and the rail can never point at different patients.
 */
export function resolveFacePatientId(
  scope: HomeScope,
  effectivePatientId: string | null,
): string | null {
  return scope === 'patient' ? effectivePatientId : null
}

/** Which single pane the NARROW (phone) layout shows — see .home-workspace. */
export type HomeLevel = 'list' | 'conversations' | 'detail'

/**
 * The drill-down depth for the narrow layout. Deliberately the RAW selections,
 * not the effective ones, and gated on `selectionTouched`: a phone drills into
 * pane 3 only for something the nurse chose in THIS load. The derived fallback
 * face is a default for the side-by-side (desktop) pane, and a RESTORED
 * selection is a desktop convenience — neither may auto-drill a phone into the
 * board on a cold launch, when she opens the app at the bedside wanting her
 * patient list.
 */
export function resolveHomeLevel({
  selectionTouched,
  selectedConversationId,
  selectedAgentFace,
  scope,
  hasValidPatientSelection,
}: {
  selectionTouched: boolean
  selectedConversationId: string | null
  selectedAgentFace: { slug: string } | null
  scope: HomeScope
  hasValidPatientSelection: boolean
}): HomeLevel {
  if (selectionTouched && (selectedConversationId != null || selectedAgentFace != null)) {
    return 'detail'
  }
  return scope === 'patient' && hasValidPatientSelection ? 'conversations' : 'list'
}
